import { PaletteRange, extendTheme } from "@mui/joy/styles";
import Color from "color";

declare module "@mui/joy/styles" {
  interface PalettePrimaryOverrides {
    blue1: true;
    blue2: true;
    solidGradientBg: true;
  }
  interface Palette {
    secondary: PaletteRange & { solidGradientBg: string };
    rowHoverBackground: string;
  }

  interface ColorPalettePropOverrides {
    secondary: true;
  }
}

const theme = extendTheme({
  fontFamily: {
    body: "IBM Plex Sans, system-ui, sans-serif",
    display: "IBM Plex Sans, system-ui, sans-serif",
  },
  typography: {
    "title-md": {
      fontSize: "1rem",
      fontWeight: "bold",
    },
    "title-lg": {
      fontSize: "1.125rem",
      fontWeight: "bold",
    },
  },
  components: {
    JoyFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: "bold",
          fontSize: 15,
        },
      },
    },
    JoyTooltip: {
      defaultProps: {
        size: "sm",
      },
      styleOverrides: {
        root: {
          backgroundColor: "#000000CC",
        },
      },
    },
    JoyDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 20,
          fontWeight: "bold",
          color: "var(--joy-palette-neutral-900)",
          justifyContent: "center",
        },
      },
    },
    JoyDialogContent: {
      styleOverrides: {
        root: {
          textAlign: "center",
        },
      },
    },
    JoyButton: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.color === "primary" &&
            ownerState.variant === "solid" && {
              backgroundImage: theme.palette.primary.solidGradientBg,
              "&.Mui-disabled": {
                opacity: 0.75,
                backgroundImage: "none",
                backgroundColor: "#5aa6ff",
                color: "white",
                ".MuiCircularProgress-root": {
                  "--CircularProgress-trackColor": "#5aa6ff",
                  "--CircularProgress-progressColor": "white",
                },
              },
            }),
        }),
      },
    },
    JoyTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          "&:not(.Mui-selected, [aria-selected='true']):hover": {
            backgroundColor: "var(--joy-palette-primary-100)",
          },
          "&.Mui-selected, &:active": {
            color: "var(--joy-palette-primary-blue1)",
            backgroundColor: "var(--joy-palette-neutral-50)",
            "&::after": {
              height: 3,
            },
          },
        },
      },
    },
  },
  spacing: 8,
  colorSchemes: {
    light: {
      palette: {
        mode: "light", // Force light mode
        primary: {
          blue1: "#2C8DFF",
          blue2: "#0075FF",
          solidColor: "#FFFFFF",
          solidBg: "var(--joy-palette-primary-blue1)",
          solidGradientBg:
            "radial-gradient(100% 2434.05% at 2.19% 0%, var(--joy-palette-primary-blue1) 0%, var(--joy-palette-primary-blue2) 100%)",

          solidHoverBg: "var(--joy-palette-primary-blue2)",
          plainColor: "var(--joy-palette-primary-blue1)",
          softBg: "#E5EFFF",
        },
        secondary: {
          solidColor: "#FFFFFF",
          solidBg: "#1E1C54",
          solidGradientBg: "radial-gradient(91.33% 55.37% at 8.67% 7.9%, #1E1C54 0%, #161541 100%)",
          solidHoverBg: Color("#1E1C54").darken(0.1).toString(),
        },
        neutral: {
          50: "#FFFFFF",
          100: "#F1F1F4",
          200: "#D4D6DE",
          300: "#C5C9D3",
          400: "#A8AEBD",
          500: "#8B93A7",
          600: "#6E7891",
          700: "#515D7B",
          800: "#343F65",
          900: "#010024",
          plainColor: "var(--joy-palette-neutral-900)",
        },
        text: {
          primary: "var(--joy-palette-neutral-900)",
          secondary: "var(--joy-palette-neutral-900)",
          tertiary: "var(--joy-palette-neutral-900)",
          icon: "var(--joy-palette-neutral-900)",
        },
        rowHoverBackground: "#F9BB282F",
      },
    },
  },
});

export default theme;
